import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Broadcast, PaperPlaneRight, WhatsappLogo, LinkedinLogo, ChatCircleDots, EnvelopeSimple, YoutubeLogo, VideoCamera, X, Sparkle, ArrowClockwise, CheckCircle } from "@phosphor-icons/react";
import Layout from "../../components/Layout";
import api, { apiErrMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

export default function SwarmQueue() {
  const [items, setItems] = useState([]);
  const [ytStatus, setYtStatus] = useState({ connected: false });
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState({});
  const [uploadItem, setUploadItem] = useState(null);
  const [ytPayload, setYtPayload] = useState({ title: "", description: "", tags: "", privacy: "unlisted" });
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const [q, y] = await Promise.all([
        api.get("/autoswarm/queue"),
        api.get("/youtube/status"),
      ]);
      setItems(q.data || []);
      setYtStatus(y.data || { connected: false });
    } catch (e) { toast.error(apiErrMsg(e, "Load failed")); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Handle ?youtube=connected|error
  useEffect(() => {
    const yt = searchParams.get("youtube");
    if (yt === "connected") {
      toast.success("YouTube connected"); load();
      searchParams.delete("youtube"); setSearchParams(searchParams, { replace: true });
    } else if (yt === "error") {
      toast.error(`YouTube connect failed: ${searchParams.get("reason") || "unknown"}`);
      searchParams.delete("youtube"); searchParams.delete("reason"); setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, load]);

  const setBusyFor = (id, key, val) => setBusy((b) => ({ ...b, [`${id}:${key}`]: val }));

  const scanNow = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/autoswarm/scan-now");
      toast.success(`Scanned ${data.hot_leads_scanned} hot leads · queued ${data.created} drafts`);
      load();
    } catch (e) { toast.error(apiErrMsg(e, "Scan failed")); }
    finally { setScanning(false); }
  };

  const dispatch = async (id, channel) => {
    setBusyFor(id, channel, true);
    try {
      const { data } = await api.post(`/autoswarm/queue/${id}/dispatch`, null, { params: { channel } });
      if (data.mocked) toast.warning(`${channel} queued (Bhash creds pending)`);
      else toast.success(`${channel} dispatched`);
      load();
    } catch (e) { toast.error(apiErrMsg(e, "Dispatch failed")); }
    finally { setBusyFor(id, channel, false); }
  };

  const dismiss = async (id) => {
    if (!window.confirm("Dismiss this draft? It won't be re-queued for the same lead.")) return;
    try { await api.post(`/autoswarm/queue/${id}/dismiss`); toast.success("Dismissed"); load(); }
    catch (e) { toast.error(apiErrMsg(e, "Dismiss failed")); }
  };

  const generateVideo = async (id) => {
    setBusyFor(id, "video", true);
    try {
      const { data } = await api.post(`/autoswarm/queue/${id}/video`, { duration: 12, size: "1024x1792", model: "sora-2" });
      toast.success("Video ready");
      // Optimistic update
      setItems((it) => it.map((x) => x.id === id ? { ...x, video_status: "ready", video_url: data.video_url } : x));
    } catch (e) { toast.error(apiErrMsg(e, "Sora generation failed (may take 2-5 min — retry once)")); }
    finally { setBusyFor(id, "video", false); }
  };

  const connectYouTube = async () => {
    try { const { data } = await api.get("/youtube/connect"); window.location.href = data.auth_url; }
    catch (e) { toast.error(apiErrMsg(e, "Connect failed")); }
  };

  const openYtUpload = (item) => {
    setUploadItem(item);
    setYtPayload({
      title: `${item.product_line} · Designed for Life`,
      description: `${item.linkedin_post || ""}\n\n${item.target_url || ""}\n\n#Shorts #MedTech #Axonix #ShelvesTech`,
      tags: "MedTech, Axonix, ShelvesTech, MakeInIndia",
      privacy: "unlisted",
    });
  };

  const submitYtUpload = async () => {
    if (!uploadItem) return;
    setBusyFor(uploadItem.id, "yt", true);
    try {
      const { data } = await api.post("/youtube/upload", {
        queue_id: uploadItem.id, title: ytPayload.title, description: ytPayload.description,
        tags: ytPayload.tags.split(",").map((t) => t.trim()).filter(Boolean), privacy: ytPayload.privacy,
      });
      toast.success(`Uploaded to YouTube: ${data.share_url}`);
      setUploadItem(null);
      load();
    } catch (e) { toast.error(apiErrMsg(e, "YouTube upload failed")); }
    finally { setBusyFor(uploadItem.id, "yt", false); }
  };

  return (
    <Layout
      title="Autonomous Swarm · Draft Queue"
      subtitle="Cron scans HOT leads every 30 min → auto-generates all channels → LinkedIn auto-posts → Email/WhatsApp waits for your approval"
      actions={
        <>
          {!ytStatus.connected ? (
            <button onClick={connectYouTube} className="hidden md:inline-flex items-center gap-2 px-3 py-2 border border-[#FF0000] text-[#FF0000] text-xs font-semibold hover:bg-[#FF0000] hover:text-white" data-testid="yt-connect">
              <YoutubeLogo size={14} weight="fill"/> Connect YouTube
            </button>
          ) : (
            <span className="hidden md:inline-flex items-center gap-1.5 text-[11px] font-mono-shelv text-[#0D9488]" data-testid="yt-connected">
              <CheckCircle size={14} weight="fill"/> YT · {ytStatus.channel_title || "connected"}
            </span>
          )}
          <button onClick={scanNow} disabled={scanning} className="inline-flex items-center gap-2 px-3 py-2 bg-[#D90429] text-white text-xs md:text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-50" data-testid="scan-now">
            <ArrowClockwise size={14} weight="bold" className={scanning ? "animate-spin" : ""}/> {scanning ? "Scanning HOT leads…" : "Scan HOT leads now"}
          </button>
        </>
      }
    >
      {items.length === 0 ? (
        <div className="border border-dashed border-black/15 bg-white p-16 text-center text-[#717680]" data-testid="queue-empty">
          <Broadcast size={40} className="mx-auto text-[#D90429] mb-3"/>
          <p className="text-sm">No drafts yet. Mark a lead as <b>HOT</b> in /leads, then click <b>Scan HOT leads now</b>.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="queue-list">
          {items.map((it) => (
            <div key={it.id} className="border border-black/10 bg-white p-5" data-testid={`queue-item-${it.id}`}>
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="font-display font-black text-lg tracking-tight">{it.lead_name}</div>
                  <span className="px-2 py-0.5 bg-[#F5F6F8] text-[10px] font-mono-shelv uppercase">{it.region.replace(/_/g, " ")}</span>
                  <span className="px-2 py-0.5 bg-[#D90429]/10 text-[#D90429] text-[10px] font-mono-shelv uppercase">{it.product_line}</span>
                </div>
                <button onClick={() => dismiss(it.id)} className="p-1.5 hover:bg-[#D90429]/10 text-[#D90429]" title="Dismiss" data-testid={`dismiss-${it.id}`}><X size={14}/></button>
              </div>
              <div className="text-[11px] text-[#717680] mb-4 flex flex-wrap gap-3">
                {it.lead_email && <span>✉ {it.lead_email}</span>}
                {it.lead_phone && <span>☎ {it.lead_phone}</span>}
                <span>· {new Date(it.created_at).toLocaleString("en-IN")}</span>
              </div>

              {/* 4 channel status pills */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <ChannelStatus icon={<EnvelopeSimple size={14}/>} label="Email" status={it.email_status} action={() => dispatch(it.id, "email")} actionLabel="Send" busy={busy[`${it.id}:email`]} disabled={!it.lead_email}/>
                <ChannelStatus icon={<WhatsappLogo size={14}/>} label="WhatsApp" status={it.whatsapp_status} action={() => dispatch(it.id, "whatsapp")} actionLabel="Send" busy={busy[`${it.id}:whatsapp`]} disabled={!it.lead_phone}/>
                <ChannelStatus icon={<LinkedinLogo size={14}/>} label="LinkedIn" status={it.linkedin_status} readonly/>
                <ChannelStatus icon={<ChatCircleDots size={14}/>} label="Quora" status={it.quora_status} readonly/>
              </div>

              {/* Copy preview accordion */}
              <details className="border border-black/10 mb-3">
                <summary className="p-2 bg-[#F5F6F8] text-[11px] font-mono-shelv uppercase tracking-widest text-[#D90429] cursor-pointer">Generated copy · click to expand</summary>
                <div className="p-3 space-y-3 text-[12px]">
                  <div><b>Email subject:</b> {it.email_subject}</div>
                  <div><b>Email body:</b><pre className="whitespace-pre-wrap font-sans text-[11px] mt-1 text-[#4A4D57]">{it.email_html?.slice(0, 400)}…</pre></div>
                  <div><b>WhatsApp:</b><pre className="whitespace-pre-wrap font-sans text-[11px] mt-1 text-[#4A4D57]">{it.whatsapp_text}</pre></div>
                  <div><b>LinkedIn:</b><pre className="whitespace-pre-wrap font-sans text-[11px] mt-1 text-[#4A4D57]">{it.linkedin_post}</pre></div>
                  <div><b>Quora:</b><pre className="whitespace-pre-wrap font-sans text-[11px] mt-1 text-[#4A4D57]">{it.quora_answer}</pre></div>
                  <div><b>Target URL:</b> <span className="break-all font-mono-shelv text-[10px]">{it.target_url}</span></div>
                </div>
              </details>

              {/* Video card */}
              <div className="border border-black/10 p-3 flex items-center gap-3">
                <VideoCamera size={20} className="text-[#F4A261]"/>
                <div className="flex-1">
                  <div className="font-mono-shelv text-[10px] uppercase tracking-widest text-[#D90429]">Sora 2 Short Video</div>
                  <div className="text-[11px] text-[#717680]">{it.video_status === "ready" ? "Ready" : it.video_status === "rendering" ? "Rendering…" : it.video_status === "failed" ? `Failed: ${it.error || "unknown"}` : "Not generated (opt-in, ~₹10 credits, 2-5 min)"}</div>
                </div>
                {it.video_status !== "ready" && (
                  <button onClick={() => generateVideo(it.id)} disabled={busy[`${it.id}:video`] || it.video_status === "rendering"} className="px-3 py-1.5 bg-[#F4A261] text-white text-xs font-semibold hover:bg-[#EF9748] disabled:opacity-50" data-testid={`video-gen-${it.id}`}>
                    {busy[`${it.id}:video`] ? "Rendering…" : "Generate video"}
                  </button>
                )}
                {it.video_status === "ready" && (
                  <>
                    <a href={it.video_url} target="_blank" rel="noreferrer" className="px-3 py-1.5 border border-black/20 text-xs font-semibold hover:bg-black hover:text-white">Preview</a>
                    {ytStatus.connected && !it.external_ids?.youtube_video_id && (
                      <button onClick={() => openYtUpload(it)} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#FF0000] text-white text-xs font-semibold hover:bg-[#CC0000]" data-testid={`yt-upload-${it.id}`}>
                        <YoutubeLogo size={12} weight="fill"/> Upload to Shorts
                      </button>
                    )}
                    {it.external_ids?.youtube_url && (
                      <a href={it.external_ids.youtube_url} target="_blank" rel="noreferrer" className="text-[#FF0000] text-xs font-semibold underline">✓ On YouTube</a>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* YouTube upload dialog */}
      <Dialog open={!!uploadItem} onOpenChange={(v) => !v && setUploadItem(null)}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Upload to YouTube Shorts</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Title *</Label><Input value={ytPayload.title} onChange={(e) => setYtPayload({ ...ytPayload, title: e.target.value })} className="rounded-none" data-testid="yt-title"/></div>
            <div>
              <Label className="text-xs">Description</Label>
              <textarea value={ytPayload.description} onChange={(e) => setYtPayload({ ...ytPayload, description: e.target.value })} className="w-full border border-black/15 p-2 rounded-none text-[12px] font-sans" rows={5} data-testid="yt-desc"/>
            </div>
            <div><Label className="text-xs">Tags (comma-sep)</Label><Input value={ytPayload.tags} onChange={(e) => setYtPayload({ ...ytPayload, tags: e.target.value })} className="rounded-none" data-testid="yt-tags"/></div>
            <div>
              <Label className="text-xs">Privacy</Label>
              <select value={ytPayload.privacy} onChange={(e) => setYtPayload({ ...ytPayload, privacy: e.target.value })} className="w-full border border-black/15 p-2 rounded-none text-sm" data-testid="yt-privacy">
                <option value="private">Private (only me)</option>
                <option value="unlisted">Unlisted (link only)</option>
                <option value="public">Public</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setUploadItem(null)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={submitYtUpload} disabled={busy[`${uploadItem?.id}:yt`]} className="px-4 py-2 bg-[#FF0000] text-white text-sm font-semibold hover:bg-[#CC0000] disabled:opacity-50" data-testid="yt-upload-submit">
              {busy[`${uploadItem?.id}:yt`] ? "Uploading…" : "Upload"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function ChannelStatus({ icon, label, status, action, actionLabel, busy, readonly, disabled }) {
  const colorMap = {
    pending_approval: { bg: "#F4A26120", fg: "#F4A261", text: "Pending" },
    dispatched: { bg: "#0D948820", fg: "#0D9488", text: "Dispatched" },
    posted: { bg: "#0D948820", fg: "#0D9488", text: "Posted" },
    queued: { bg: "#71768020", fg: "#717680", text: "Queued" },
    queued_bhash: { bg: "#F4A26120", fg: "#F4A261", text: "Bhash-queued" },
    in_inbox: { bg: "#F4A26120", fg: "#F4A261", text: "In Quora Inbox" },
    skipped: { bg: "#71768020", fg: "#717680", text: "Skipped" },
    failed: { bg: "#D9042920", fg: "#D90429", text: "Failed" },
  };
  const c = colorMap[status] || { bg: "#71768020", fg: "#717680", text: status };
  return (
    <div className="border border-black/10 p-2.5 flex items-center gap-2">
      <span style={{ color: c.fg }}>{icon}</span>
      <div className="flex-1">
        <div className="text-[11px] font-mono-shelv uppercase tracking-widest text-[#717680]">{label}</div>
        <div className="text-[11px] font-semibold" style={{ color: c.fg }}>{c.text}</div>
      </div>
      {!readonly && status === "pending_approval" && (
        <button onClick={action} disabled={busy || disabled} className="px-2 py-1 bg-[#D90429] text-white text-[10px] font-semibold hover:bg-[#EF233C] disabled:opacity-40" title={disabled ? "Contact missing" : ""}>
          {busy ? "…" : actionLabel}
        </button>
      )}
    </div>
  );
}
